import {
  COLORS,
  drawArrow,
  drawDashedLine,
  drawPill,
  drawText,
  hexToRgba,
  roundedRect,
} from './shared.js';

function geometry(width, height) {
  const left = width * 0.07;
  const right = width * 0.93;
  return {
    left,
    right,
    width: right - left,
    y: height * 0.56,
    scale: (right - left) / 10,
  };
}

function cartScreenX(x, geo) {
  return geo.left + ((x + 5) / 10) * geo.width;
}

export const collisionRenderer = {
  trailPoint() {
    return null;
  },

  draw(ctx, frame) {
    const { width, height, state, params, metrics, options, accent } = frame;
    const geo = geometry(width, height);
    const cartWidth = Math.min(74, Math.max(40, geo.scale * 0.62));
    const cartHeight = 34;
    const y = geo.y - cartHeight;
    const x1 = cartScreenX(state.x1, geo);
    const x2 = cartScreenX(state.x2, geo);
    const colorB = COLORS.sky;

    // 轨道
    ctx.save();
    ctx.strokeStyle = 'rgba(167, 202, 226, 0.32)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(geo.left, geo.y + 2);
    ctx.lineTo(geo.right, geo.y + 2);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(167, 202, 226, 0.1)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 20; i += 1) {
      const x = geo.left + (i / 20) * geo.width;
      ctx.beginPath();
      ctx.moveTo(x, geo.y + 2);
      ctx.lineTo(x, geo.y + (i % 10 === 0 ? 14 : 8));
      ctx.stroke();
    }
    for (let meter = -5; meter <= 5; meter += 1) {
      const x = cartScreenX(meter, geo);
      drawText(ctx, `${meter}`, x, geo.y + 26, {
        color: meter === 0 ? 'rgba(200,224,242,.65)' : 'rgba(160,191,214,.32)',
        font: '500 9px "Cascadia Code", monospace',
        align: 'center',
      });
    }
    ctx.restore();

    // 碰撞中心参考
    drawDashedLine(ctx, { x: width / 2, y: geo.y - 88 }, { x: width / 2, y: geo.y + 12 }, {
      color: 'rgba(148,190,220,.12)',
      dash: [2, 7],
    });

    // 质心标记
    const comX = cartScreenX(metrics.extras.comPosition, geo);
    if (comX >= geo.left - 20 && comX <= geo.right + 20) {
      ctx.save();
      ctx.fillStyle = 'rgba(94, 234, 212, .55)';
      ctx.beginPath();
      ctx.moveTo(comX, geo.y + 46);
      ctx.lineTo(comX - 5, geo.y + 55);
      ctx.lineTo(comX + 5, geo.y + 55);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      drawText(ctx, `质心 v=${metrics.extras.comVelocity.toFixed(2)} m/s`, comX, geo.y + 68, {
        color: 'rgba(94,234,212,.65)',
        font: '500 10px "Cascadia Code", monospace',
        align: 'center',
      });
    }

    const drawCart = (x, mass, velocity, color, label, isA) => {
      if (x < -cartWidth || x > width + cartWidth) return;
      ctx.save();
      // 阴影
      ctx.fillStyle = 'rgba(0,0,0,.28)';
      ctx.beginPath();
      ctx.ellipse(x, geo.y + 4, cartWidth * 0.5, 6, 0, 0, Math.PI * 2);
      ctx.fill();

      const gradient = ctx.createLinearGradient(0, y, 0, y + cartHeight);
      gradient.addColorStop(0, hexToRgba(color, 0.28));
      gradient.addColorStop(1, hexToRgba(color, 0.09));
      roundedRect(ctx, x - cartWidth / 2, y, cartWidth, cartHeight, 8);
      ctx.fillStyle = gradient;
      ctx.fill();
      ctx.strokeStyle = hexToRgba(color, 0.72);
      ctx.lineWidth = 1.4;
      ctx.shadowColor = hexToRgba(color, 0.28);
      ctx.shadowBlur = 12;
      ctx.stroke();
      ctx.shadowBlur = 0;

      // 顶部高光
      ctx.strokeStyle = hexToRgba(color, 0.4);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x - cartWidth * 0.34, y + 5);
      ctx.lineTo(x + cartWidth * 0.34, y + 5);
      ctx.stroke();

      drawText(ctx, label, x, y + cartHeight * 0.42, {
        color,
        font: `700 ${isA ? 15 : 14}px "Cascadia Code", monospace`,
        align: 'center',
      });
      drawText(ctx, `${mass.toFixed(1)} kg`, x, y + cartHeight * 0.72, {
        color: 'rgba(222,238,248,.58)',
        font: '500 9px "Cascadia Code", monospace',
        align: 'center',
      });

      // 轮子
      ctx.fillStyle = 'rgba(217,236,249,.42)';
      for (const offset of [-cartWidth * 0.28, cartWidth * 0.28]) {
        ctx.beginPath();
        ctx.arc(x + offset, geo.y + 1, 4, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();

      // 速度箭头
      if (Math.abs(velocity) > 0.015) {
        const dir = Math.sign(velocity);
        const arrowLength = Math.min(100, Math.max(16, Math.abs(velocity) * 12));
        const startX = x + dir * (cartWidth / 2 + 4);
        drawArrow(
          ctx,
          { x: startX, y: y - 14 },
          { x: startX + dir * arrowLength, y: y - 14 },
          {
            color,
            width: 2,
            head: 7,
            label: `${velocity > 0 ? '+' : ''}${velocity.toFixed(2)} m/s`,
            labelOffset: { x: dir * 20, y: -13 },
          },
        );
      }
    };

    drawCart(x1, params.mass1, state.v1, accent, 'A', true);
    drawCart(x2, params.mass2, state.v2, colorB, 'B', false);

    // 碰撞闪光
    if (state.lastCollisionAt !== null) {
      const age = state.t - state.lastCollisionAt;
      if (age >= 0 && age < 0.8) {
        const alpha = (1 - age / 0.8) * 0.8;
        const cx = cartScreenX(state.lastCollisionX, geo);
        const cy = geo.y - cartHeight / 2;
        ctx.save();
        ctx.globalAlpha = alpha;
        const burst = ctx.createRadialGradient(cx, cy, 2, cx, cy, 58);
        burst.addColorStop(0, 'rgba(255,255,255,.9)');
        burst.addColorStop(0.25, hexToRgba(accent, 0.42));
        burst.addColorStop(1, 'rgba(251,191,36,0)');
        ctx.fillStyle = burst;
        ctx.beginPath();
        ctx.arc(cx, cy, 58, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,.6)';
        ctx.lineWidth = 1.2;
        for (let i = 0; i < 10; i += 1) {
          const angle = (i / 10) * Math.PI * 2;
          const inner = 16 + age * 32;
          const outer = inner + 12;
          ctx.beginPath();
          ctx.moveTo(cx + Math.cos(angle) * inner, cy + Math.sin(angle) * inner);
          ctx.lineTo(cx + Math.cos(angle) * outer, cy + Math.sin(angle) * outer);
          ctx.stroke();
        }
        ctx.restore();
      }
    }

    // 底部读数
    const bottomY = height - 30;
    drawPill(ctx, `Σp ${metrics.momentum.total.toFixed(2)} kg·m/s`, width * 0.5 - 152, bottomY, {
      color: COLORS.text,
      bg: 'rgba(7,18,30,.7)',
      border: 'rgba(148,190,220,.14)',
      font: '600 11px "Cascadia Code", monospace',
    });
    drawPill(ctx, `Eₖ ${metrics.energy.kinetic.toFixed(2)} J`, width * 0.5 - 8, bottomY, {
      color: accent,
      bg: hexToRgba(accent, 0.07),
      border: hexToRgba(accent, 0.24),
      font: '600 11px "Cascadia Code", monospace',
    });
    drawPill(
      ctx,
      `e = ${params.restitution.toFixed(2)} ${params.restitution >= 0.999 ? '完全弹性' : '非弹性'}`,
      width * 0.5 + 104,
      bottomY,
      {
        color: params.restitution >= 0.999 ? COLORS.mint : COLORS.amber,
        bg: 'rgba(7,18,30,.7)',
        border: 'rgba(148,190,220,.14)',
        font: '600 11px "Cascadia Code", monospace',
      },
    );
  },
};
